import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as store from '@attain-apps/fe/web/main/lib/store'

import * as dms from '../../../dms'

import { getActionsConfig } from './actions'
import { Api } from './api'
import * as effects from './effects'
import * as extraReducers from './extra-reducers'
import { Selectors } from './selectors'
import { State } from './state'

/** Configurable plugin for selecting records within one Space dimension. */
export abstract class Plugin<
  TTypeParams extends Plugin.TypeParams = Plugin.TypeParams,
  TId extends ec.plugins.Plugin.Id.Custom = ec.plugins.Plugin.Id.Custom,
> extends ec.plugins.Plugin<
  TTypeParams,
  TId,
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  /** @see {@link Api} */
  public readonly api = ngCore.inject(Api)

  /** The ID of the dimension this feature operates on */
  public readonly dimensionId: ents.core.dms.models.Dimension.Id

  /** In-memory selections keyed by DMS entity ID. */
  protected readonly selectionStorage =
    new store.storage.included.InMemoryStorage({
      parent: this.feature,
      properties: {
        lookup: store.storage.lookup<
          ents.core.dms.models.Entity.Id,
          readonly ents.core.dms.data.Record.Id[]
        >({
          get: (key, ids = []) => ids,
        }),
      },
      name: 'Selection',
    })

  /** Convenience accessor for the plugin feature's action creators. */
  public get actions(): this['feature']['actions'] {
    return this.feature.actions
  }

  /** Convenience accessor for the plugin feature's selectors. */
  public get selectors(): this['feature']['selectors'] {
    return this.feature.selectors
  }

  protected constructor({
    collection,
    dimensionId,
    id,
  }: Plugin.Config<TTypeParams, TId>) {
    const schemaFeature = ngCore.inject(dms.schema.Feature)

    super({
      actions: getActionsConfig(),
      collection,
      id,
      initialState: State.INITIAL,
      selectors: () =>
        new Selectors(this, this.selectionStorage, schemaFeature),
    })
    this.dimensionId = dimensionId

    this.feature.registerExtraReducers(extraReducers.resetOnContextChange(this))

    this.feature.registerEffects(
      effects.clearSelectionOnContextChange(this),
      effects.executeLoad(this),
      effects.loadDescendantOptionsOnSelection(schemaFeature),
      effects.loadOptions(this, schemaFeature),
      effects.loadOptionsOnContextChange(this),
      effects.pruneInvalidSelections(),
      effects.resetSelections(this.selectionStorage),
      effects.setSelection(this.selectionStorage),
    )
  }
}

export namespace Plugin {
  export type TypeParams = ec.models.TypeParams.Constraint<{
    context: ents.core.admin.Context.ForScenario | undefined
  }>

  export interface Config<
    TTypeParams extends Plugin.TypeParams,
    TId extends ec.plugins.Plugin.Id.Custom,
  > {
    readonly collection: ec.Collection<TTypeParams>
    readonly dimensionId: ents.core.dms.models.Dimension.Id
    readonly id: TId
  }
}
